import { createElement, type ReactNode } from "react";
import { Euro, Receipt } from "lucide-react";
import type {
  AvgCheckResponse,
  RevenueByServiceResponse,
  RevenueDistributionResponse,
  TotalRevenueResponse,
} from "@/types/analytics-revenue";
import { at } from "../../utils/analyticsI18n";
import type { MetricCardTrend } from "../shared/MetricCard";
import type { DonutSegment, StackedRevenuePoint } from "./mockData";
import { REVENUE_COLORS } from "./constants";
import { formatRevenueCurrency } from "./revenueCurrency";

export type RevenueKpiMetric = {
  title: string;
  value: string;
  delta?: string;
  trend?: MetricCardTrend;
  icon: ReactNode;
};

const DELTA_SUFFIX = () =>
  at("common.fromPreviousPeriod", " from previous period");

function toMajorUnits(amount: number, divisor: number): number {
  if (!divisor) {
    return amount;
  }
  return amount / divisor;
}

function getTotalNetForCurrency(
  response: TotalRevenueResponse | undefined,
  currency: string,
): number {
  const row = response?.totals.byCurrency.find(
    (item) => item.currency === currency,
  );
  if (!row || !response) {
    return 0;
  }

  return toMajorUnits(row.net, response.amountDivisor);
}

function getAvgCheckForCurrency(
  response: AvgCheckResponse | undefined,
  currency: string,
): number {
  const row = response?.totals.byCurrency.find(
    (item) => item.currency === currency,
  );
  if (!row || !response) {
    return 0;
  }

  return toMajorUnits(row.avgCheck ?? 0, response.amountDivisor);
}

function percentChange(current: number, previous: number): number | null {
  if (current === previous) {
    return 0;
  }

  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }

  return ((current - previous) / previous) * 100;
}

function formatPercentDelta(change: number | null): string | undefined {
  if (change === null) {
    return undefined;
  }

  const sign = change > 0 ? "+" : "";
  const formatted = change.toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });

  return `${sign}${formatted}%${DELTA_SUFFIX()}`;
}

function trendFromChange(change: number | null): MetricCardTrend {
  if (change === null || change === 0) {
    return "neutral";
  }

  return change > 0 ? "positive" : "negative";
}

function buildRevenueMetricDelta(
  current: number,
  previous: number,
): Pick<RevenueKpiMetric, "delta" | "trend"> {
  const change = percentChange(current, previous);

  return {
    delta: formatPercentDelta(change),
    trend: trendFromChange(change),
  };
}

export function mapRevenueKpiMetrics(
  total: TotalRevenueResponse | undefined,
  avgCheck: AvgCheckResponse | undefined,
  previousTotal: TotalRevenueResponse | undefined,
  previousAvgCheck: AvgCheckResponse | undefined,
  currency: string,
  includeDeltas: boolean,
): RevenueKpiMetric[] {
  const currentTotal = getTotalNetForCurrency(total, currency);
  const currentAvgCheck = getAvgCheckForCurrency(avgCheck, currency);
  const previousTotalAmount = getTotalNetForCurrency(previousTotal, currency);
  const previousAvgCheckAmount = getAvgCheckForCurrency(
    previousAvgCheck,
    currency,
  );
  const totalDelta = includeDeltas
    ? buildRevenueMetricDelta(currentTotal, previousTotalAmount)
    : {};
  const avgCheckDelta = includeDeltas
    ? buildRevenueMetricDelta(currentAvgCheck, previousAvgCheckAmount)
    : {};

  return [
    {
      title: "Total Revenue",
      value: formatRevenueCurrency(currentTotal, currency),
      ...totalDelta,
      icon: createElement(Euro),
    },
    {
      title: "Avg. check per reservation",
      value: formatRevenueCurrency(currentAvgCheck, currency),
      ...avgCheckDelta,
      icon: createElement(Receipt),
    },
  ];
}

const CATEGORY_META = {
  GUEST_SERVICES: {
    key: "guestServices",
    name: "Guest Services",
    fill: REVENUE_COLORS.guestServices,
  },
  GUESTERIA_PARTNERS: {
    key: "guesteriaPartners",
    name: "Guesteria partners",
    fill: REVENUE_COLORS.guesteriaPartners,
  },
  EXTERNAL_SERVICES: {
    key: "externalServices",
    name: "External services",
    fill: REVENUE_COLORS.externalServices,
  },
} as const;

export function mapRevenueDistributionSegments(
  response: RevenueDistributionResponse | undefined,
  currency: string,
): DonutSegment[] {
  const divisor = response?.amountDivisor ?? 1;
  const bySource = response?.totals.bySource ?? [];

  return Object.entries(CATEGORY_META).map(([source, meta]) => {
    const amountMinor = bySource
      .filter((item) => item.source === source && item.currency === currency)
      .reduce((sum, item) => sum + item.amount, 0);

    return {
      key: meta.key,
      name: meta.name,
      value: toMajorUnits(amountMinor, divisor),
      fill: meta.fill,
    };
  });
}

export function mapRevenueByChannelSegments(
  response: RevenueByServiceResponse | undefined,
  currency: string,
): DonutSegment[] {
  const divisor = response?.amountDivisor ?? 1;
  const rows = (response?.series ?? []).filter(
    (row) => row.currency === currency,
  );
  const appMinor = rows
    .filter((row) => row.source === "STRIPE")
    .reduce((sum, row) => sum + row.net, 0);
  const whatsappMinor = rows
    .filter((row) => row.source === "PARTNER")
    .reduce((sum, row) => sum + row.net, 0);

  return [
    {
      key: "app",
      name: "App",
      value: toMajorUnits(appMinor, divisor),
      fill: REVENUE_COLORS.app,
    },
    {
      key: "whatsapp",
      name: "WhatsApp",
      value: toMajorUnits(whatsappMinor, divisor),
      fill: REVENUE_COLORS.whatsapp,
    },
  ];
}

export function mapRevenueByServiceSeries(
  response: RevenueByServiceResponse | undefined,
  currency: string,
): StackedRevenuePoint[] {
  const divisor = response?.amountDivisor ?? 1;
  const dayMap = new Map<string, StackedRevenuePoint>();

  for (const row of response?.series ?? []) {
    if (row.currency !== currency) {
      continue;
    }

    const point = dayMap.get(row.day) ?? {
      day: row.day,
      guestServices: 0,
      guesteriaPartners: 0,
      externalServices: 0,
      app: 0,
      whatsapp: 0,
    };

    const value = toMajorUnits(row.net, divisor);

    if (row.source === "STRIPE") {
      point.app += value;
    } else if (row.source === "PARTNER") {
      point.whatsapp += value;
    }

    point.guestServices += value;

    dayMap.set(row.day, point);
  }

  return [...dayMap.values()].sort((a, b) => a.day.localeCompare(b.day));
}

export function mapRevenueSeasonalitySeries(
  response: RevenueDistributionResponse | undefined,
  currency: string,
): StackedRevenuePoint[] {
  const divisor = response?.amountDivisor ?? 1;

  return (response?.series ?? []).map((row) => {
    const amountBySource = new Map(
      row.bySource
        .filter((item) => item.currency === currency)
        .map((item) => [item.source, toMajorUnits(item.amount, divisor)]),
    );

    return {
      day: row.day,
      guestServices: amountBySource.get("GUEST_SERVICES") ?? 0,
      guesteriaPartners: amountBySource.get("GUESTERIA_PARTNERS") ?? 0,
      externalServices: amountBySource.get("EXTERNAL_SERVICES") ?? 0,
      app: 0,
      whatsapp: 0,
    };
  });
}
